import { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { t, useLanguage } from '@/app/utils/i18n';
import type { AqlGrowthMeasurement } from '@/app/services/growth-service';

interface SizeDistributionChartProps {
  measurements: AqlGrowthMeasurement[];
  loading?: boolean;
}

const MIN_VALID_SAMPLE = 30;
const BUCKET_TARGET_COUNT = 14;
const BUCKET_MIN_WIDTH_MM = 1;

interface DistributionResult {
  bins: { label: string; mid: number; count: number }[];
  validCount: number;
  rejectedCount: number;
  hasEnoughSample: boolean;
}

const buildDistribution = (measurements: AqlGrowthMeasurement[]): DistributionResult => {
  const valid: number[] = [];
  let rejectedCount = 0;

  for (const item of measurements) {
    if (!item.is_valid) {
      rejectedCount += 1;
      continue;
    }
    if (item.length_mm === null || item.length_mm === undefined || Number.isNaN(item.length_mm)) continue;
    valid.push(item.length_mm);
  }

  if (!valid.length) {
    return { bins: [], validCount: 0, rejectedCount, hasEnoughSample: false };
  }

  const min = Math.min(...valid);
  const max = Math.max(...valid);
  const range = max - min;
  const proposedWidth = range > 0 ? range / BUCKET_TARGET_COUNT : BUCKET_MIN_WIDTH_MM;
  const width = Math.max(BUCKET_MIN_WIDTH_MM, Number(proposedWidth.toFixed(2)));
  const bucketCount = range === 0 ? 1 : Math.max(1, Math.ceil(range / width));

  const buckets = Array.from({ length: bucketCount }, (_, idx) => {
    const start = min + idx * width;
    const end = start + width;
    const mid = (start + end) / 2;
    return {
      start,
      end,
      mid,
      label: `${start.toFixed(0)}–${end.toFixed(0)}`,
      count: 0
    };
  });

  for (const length of valid) {
    let idx = Math.floor((length - min) / width);
    if (idx >= buckets.length) idx = buckets.length - 1;
    if (idx < 0) idx = 0;
    buckets[idx].count += 1;
  }

  return {
    bins: buckets.map(({ label, mid, count }) => ({ label, mid, count })),
    validCount: valid.length,
    rejectedCount,
    hasEnoughSample: valid.length >= MIN_VALID_SAMPLE
  };
};

export function SizeDistributionChart({ measurements, loading }: SizeDistributionChartProps) {
  const language = useLanguage();
  const tr = (key: string, params?: Record<string, unknown>) => t('vision', key, language, params);
  const distribution = useMemo(() => buildDistribution(measurements), [measurements]);

  if (loading) {
    return (
      <div className="min-w-0 rounded-2xl border border-border bg-card p-4 shadow-sm">
        <div className="h-5 w-44 animate-pulse rounded bg-muted/70" />
        <div className="mt-4 h-56 animate-pulse rounded bg-muted/40" />
      </div>
    );
  }

  return (
    <div className="min-w-0 rounded-2xl border border-border bg-card p-4 shadow-sm">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-[240px] flex-1">
          <h3 className="text-base font-semibold text-foreground">{tr('sizeDistribution')}</h3>
          <p className="text-xs text-muted-foreground">
            {tr('sizeDistributionSubtitle')}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-3 text-[11px] text-muted-foreground">
          <span>
            {tr('valid')} <span className="font-mono font-semibold text-foreground">{distribution.validCount}</span>
          </span>
          <span>
            {tr('rejectedMeasurements')} <span className="font-mono font-semibold text-foreground">{distribution.rejectedCount}</span>
          </span>
        </div>
      </div>

      {distribution.bins.length === 0 ? (
        <p className="mt-4 rounded-lg border border-dashed border-border bg-muted/30 p-4 text-sm text-muted-foreground">
          {tr('noValidMeasurementsForSelection')}
        </p>
      ) : (
        <div className="mt-3 h-64 min-w-0">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={distribution.bins} margin={{ top: 6, right: 12, bottom: 4, left: 0 }}>
              <CartesianGrid stroke="currentColor" strokeOpacity={0.08} vertical={false} />
              <XAxis
                dataKey="label"
                stroke="currentColor"
                strokeOpacity={0.45}
                fontSize={10}
                tickLine={false}
                axisLine={false}
                interval={Math.max(0, Math.floor(distribution.bins.length / 7) - 1)}
                minTickGap={12}
                height={32}
              />
              <YAxis
                stroke="currentColor"
                strokeOpacity={0.45}
                fontSize={10}
                tickLine={false}
                axisLine={false}
                width={32}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{
                  background: 'var(--popover, #0b1324)',
                  border: '1px solid var(--border, #263753)',
                  borderRadius: 8,
                  fontSize: 12
                }}
                labelStyle={{ color: 'var(--muted-foreground, #94a3b8)' }}
                itemStyle={{ color: 'var(--popover-foreground, #f8fafc)' }}
                labelFormatter={(label) => `${label} mm`}
                formatter={(value: number) => [tr('fishCount', { count: value }), tr('count')]}
              />
              <Bar dataKey="count" fill="#22d3ee" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {!distribution.hasEnoughSample && distribution.validCount > 0 ? (
        <p className="mt-3 rounded-lg border border-amber-400/40 bg-amber-500/10 px-3 py-2 text-xs font-medium text-amber-800 dark:text-amber-100">
          {tr('insufficientSampleSize')}
        </p>
      ) : null}
    </div>
  );
}
